import {
  ShapedClass,
  dot,
  searchClasses,
  shapeClass,
} from "@/lib/class-utilities";
import { useRouter } from "@/lib/next-nprogress";
import useIndexedDB from "@/lib/useIndexedDB";
import { Description, DialogTitle } from "@headlessui/react";
import {
  ArchiveBoxIcon,
  FolderPlusIcon,
  HomeIcon,
  LifebuoyIcon,
  PencilIcon,
  PlusCircleIcon,
  PrinterIcon,
  Squares2X2Icon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Squares2X2Icon as Squares2X2IconSolid } from "@heroicons/react/24/solid";
import cuid from "cuid";
import { VideoKeys, videos } from "@/lib/resources";
import useLocalStorageState from "@/lib/useLocalStorageState";
import {
  classNames,
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  searchRecipes,
  searchTags,
  sortAlphabetically,
  sortAlphabeticallyReversed,
  sortByClass,
  sortByClassReversed,
  sortByFirstUpdated,
  sortByLastUpdated,
  sortByNewest,
  sortByOldest,
  sortByTag,
  sortByTagReversed,
} from "@/lib/util";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  OrderCarouselQuery,
  useMoveManyRecipesToCollectionMutation,
  useOrderCarouselQuery,
  useUpdateSchoolMutation,
  useUpdateUserMutation,
} from "@/queries/autogenerate/graphql";
import { Fragment, useEffect, useState } from "react";
import toast from "react-hot-toast";
import AsyncSelect from "react-select/async";
import styled from "styled-components";
import { Divider, SearchInputIcon } from "../Commons";
import DialogComponent from "../Dialog";
import ErrorMessage from "../ErrorMessage";
import FocusTextInput from "../FocusTextInput";
import { CurrentUser } from "../Page";
import { TitleBar } from "../styles/Layout";
import Tooltip from "../styles/Tooltip";
import DishIcon from "../svg/DishIcon";
import ExcludeIcon from "../svg/ExcludeIcon";
import ExploreIcon from "../svg/ExploreIcon";
import HistoryIcon from "../svg/HistoryIcon";
import UndrawChef from "../svg/UndrawChef";
import RecipesGrid from "./RecipesGrid";
import RecipesList from "./RecipesList";
import { BulkOrder } from "./SelectionAction";
import Settings, {
  LockAllRecipesButton,
  UnlockAllRecipesButton,
} from "./Settings";
import { FolderSelectInput, getPathToFolder } from "./util";

const SearchContainer = styled.div`
  min-height: 38px;
  min-width: 150px;
  /* max-width: 80vw; */
  width: 350px;

  input {
    width: 100%;

    backdrop-filter: saturate(180%) blur(20px);
  }
`;

const recipeKeys: VideoKeys[] = [
  "sharing-discovering-recipes",
  "upload-recipes",
  "adding-recipe-manually",
  "ingredient-measurements",
  "troubleshooting",
];
const recipeVideos = recipeKeys.map((key) => videos[key]);

interface Props {
  user: CurrentUser;
  // user: Required<CurrentUserQuery>["me"];
}

const RecipesPage: React.FC<Props> = ({ user }) => {
  const searchParams = useSearchParams();

  const folderID = searchParams?.get("folder");
  const folder = folderID
    ? user.school?.recipeCollections.find((c) => c.id === folderID)
    : null;

  // For artificial infinite loading
  const [limit, setLimit] = useState(30);
  const [fragmentID, setFragmentID] = useState<string | null>(null);
  useEffect(() => {
    const match = window.location.hash.match(/#\S+/);
    const id = (match && match[0]?.replace("#", "")) || null;

    setFragmentID(id);

    return () => {};
  }, [searchParams]);

  const [view, setView] = useIndexedDB("recipesView", "grid");

  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useLocalStorageState("recipesOrderBy", {
    label: "Alphabetical",
    value: "name_ASC",
  });

  const unsortedClasses =
    isAdmin(user) || isAssistant(user)
      ? user.school?.classes || []
      : user.school?.classes.filter((c) =>
          c.users.some((u) => u.id === user.id)
        ) || [];

  const [isFilterExclusive, setFilterExclusive] = useIndexedDB(
    "recipeFilterExclusive",
    false
  );
  const [filteredTags, setFilteredTags] = useState<
    { value: string; label: string; __isNew__: boolean; type: "tag" }[]
  >([]);
  const [filteredClasses, setFilteredClasses] = useState<ShapedClass[]>([]);
  const [currentClass, setCurrentClass] = useState<
    | ShapedClass
    | { value: string; label: string; __isNew__: boolean; type: "tag" }
    | null
  >(null);

  const [supportOpen, setSupportOpen] = useState(false);

  const [{}, updateUser] = useUpdateUserMutation();

  const { needsWelcome, introRecipe } = user;
  useEffect(() => {
    if (needsWelcome) {
      setSupportOpen(false);
    } else if (introRecipe) {
      setTimeout(() => {
        setSupportOpen(true);
      }, 2 * 1000);
    }
  }, [needsWelcome]);

  const [videoId, setVideoId] = useState(recipeVideos[0].id);
  const [autoPlay, setAutoPlay] = useState(false);

  const [rawSelectedRecipes, setSelectedRecipes] = useState<string[]>([]);

  const [createRecipeFolderOpen, setCreateRecipeFolderOpen] = useState(false);
  const openCreateRecipeFolder = () => setCreateRecipeFolderOpen(true);
  const closeCreateRecipeFolder = () => setCreateRecipeFolderOpen(false);

  const [editRecipeFolderOpen, setEditRecipeFolderOpen] = useState<
    string | null
  >(null);
  const openEditRecipeFolder = (id: string) => setEditRecipeFolderOpen(id);
  const closeEditRecipeFolder = () => setEditRecipeFolderOpen(null);

  const [deleteRecipeFolderOpen, setDeleteRecipeFolderOpen] = useState<
    string | null
  >(null);
  const openDeleteRecipeFolder = (id: string) => setDeleteRecipeFolderOpen(id);
  const closeDeleteRecipeFolder = () => setDeleteRecipeFolderOpen(null);

  const [{ fetching, error, data }, reexecuteQuery] = useOrderCarouselQuery({
    variables: { schoolID: user.school?.id || "" },
    requestPolicy: "cache-and-network",
  });

  // useEffect(() => {
  //   reexecuteQuery({ requestPolicy: "network-only" });
  // }, []);

  if (!data) {
    if (fetching) return <p>Loading...</p>;
    if (error) return <ErrorMessage error={error} />;
  }
  if (!data) return null;

  const { recipes, tags } = data;
  // Filter recipes based on access permissions
  // permSeeTeacherRecipes (see recipes made by teachers)
  // permSeeOtherStudentRecipes (see recipes made by other students)
  // Also, filter out archived recipes
  const folderRecipes = search
    ? recipes
    : folder
    ? recipes.filter((r) => r.recipeCollection?.id === folder.id)
    : recipes.filter((r) => !r.recipeCollection);
  const filteredRecipes = folderRecipes
    ? folderRecipes
        .filter((r) =>
          search
            ? true
            : filteredClasses.length || filteredTags.length
            ? isFilterExclusive
              ? filteredClasses.every((fc) =>
                  r.classes.some((rc) => rc.id === fc.obj.id)
                ) &&
                filteredTags.every((ft) =>
                  r.tags.some((rt) => rt.id === ft.value)
                )
              : r.classes.some((rc) =>
                  filteredClasses.some((fc) => fc.obj.id === rc.id)
                ) ||
                r.tags.some((rt) =>
                  filteredTags.some((ft) => ft.value === rt.id)
                )
            : true
        )
        .filter((r) =>
          isStudent(user) && r.user && r.user.id != user.id
            ? (!user.school?.permSeeOtherStudentRecipes && isStudent(r.user)) ||
              (!user.school?.permSeeTeacherRecipes && isTeacher(r.user))
              ? false
              : true
            : true
        )
        .filter((r) => !r.archivedAt)
    : [];
  const searchedRecipes = searchRecipes(search, filteredRecipes);

  const sortFunctionMap = new Map<string, any>([
    ["name_ASC", sortAlphabetically],
    ["name_DESC", sortAlphabeticallyReversed],
    ["createdAt_ASC", sortByOldest],
    ["createdAt_DESC", sortByNewest],
    ["updatedAt_ASC", sortByFirstUpdated],
    ["updatedAt_DESC", sortByLastUpdated],
    ["tag_ASC", sortByTag],
    ["tag_DESC", sortByTagReversed],
    ["class_ASC", sortByClass],
    ["class_DESC", sortByClassReversed],
  ]);
  const sortedRecipes = [...searchedRecipes].sort(
    sortFunctionMap.get(orderBy.value)
  );

  const selectedRecipes = rawSelectedRecipes.filter((r) =>
    sortedRecipes.some((sr) => sr.id === r)
  );

  const folderSortFunctionMap = new Map<string, any>([
    ["name_ASC", sortAlphabetically],
    ["name_DESC", sortAlphabeticallyReversed],
    ["createdAt_ASC", sortByOldest],
    ["createdAt_DESC", sortByNewest],
    ["updatedAt_ASC", sortByFirstUpdated],
    ["updatedAt_DESC", sortByLastUpdated],
    ["tag_ASC", sortAlphabetically],
    ["tag_DESC", sortAlphabetically],
    ["class_ASC", sortAlphabetically],
    ["class_DESC", sortAlphabetically],
  ]);
  const sortedFolders = [...(user.school?.recipeCollections || [])].sort(
    folderSortFunctionMap.get(orderBy.value)
  );

  return (
    <div id="recipes">
      <div className="sticky top-0 z-20">
        <TitleBar>
          <header className="flex items-center gap-4">
            <h2 className="flex items-center justify-center gap-1.5">
              <DishIcon className="mr-2 w-8" /> Recipes
              {recipes ? <span>({recipes.length})</span> : null}
            </h2>
            {folder ? (
              <div className="flex items-center gap-0">
                <Link prefetch={false} href="/recipes">
                  <HomeIcon className="w-5 transform hover:scale-110 hover:text-gray-900" />
                </Link>
                {getPathToFolder(folder, sortedFolders).map((id) => {
                  const tempFolder = sortedFolders.find((c) => c.id === id);

                  return (
                    <Fragment key={id}>
                      <svg
                        className="mx-0.5 h-5 text-gray-300"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M11 0h3L9 20H6l5-20z" />
                      </svg>
                      {tempFolder?.id === folder.id ? (
                        <p className="font-medium text-gray-400">
                          {tempFolder.name}
                        </p>
                      ) : (
                        <Link
                          prefetch={false}
                          href={`/recipes?folder=${tempFolder?.id}`}
                        >
                          <p className="font-medium text-gray-500 hover:text-gray-700">
                            {tempFolder?.name}
                          </p>
                        </Link>
                      )}
                    </Fragment>
                  );
                })}
                <div className="flex items-center gap-1 px-2">
                  <Tooltip content="Delete Folder">
                    <button onClick={() => openDeleteRecipeFolder(folder.id)}>
                      <TrashIcon className="w-4 transform text-orange-600 hover:scale-110 hover:text-orange-700" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Edit Folder">
                    <button onClick={() => openEditRecipeFolder(folder.id)}>
                      <PencilIcon className="w-4 transform hover:scale-110 hover:text-gray-900" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ) : null}
          </header>
          {/* TODO: ability to select recipes and have them be locked/unlocked  */}
          <div className="flex items-center">
            <Tooltip content="Support Resources">
              <button className="" onClick={() => setSupportOpen(true)}>
                <LifebuoyIcon className="icon-md text-amber-500 hover:text-amber-600" />
              </button>
            </Tooltip>
            {/* <MajorModal
              isOpen={supportOpen}
              onClose={async () => {
                setSupportOpen(false);
                await updateUser({
                  where: { id: user.id },
                  data: { introRecipe: { set: false } },
                });
              }}
            >
              <div className="flex justify-between gap-5 lg:flex-col">
                <div className="w-56 shrink-0 xl:w-80 lg:w-full">
                  <h1 className="text-4xl font-normal">Recipes</h1>
                  <p className="mt-5 text-2xl font-normal text-gray-600">
                    An important part of streamlining your food ordering process
                    is having a collection of recipes from which you can make
                    orders.
                  </p>
                  <p className="mt-4 text-2xl font-normal text-gray-600">
                    ClassCart makes it easy to manage a growing collection of
                    recipes. You can add something of your own, discover a
                    shared recipe, or find something online.
                  </p>
                  <p className="mt-10 text-2xl font-normal text-gray-800">
                    Learn more with these short video guides
                    <ArrowDownIcon className="mx-1.5 w-2.5 animate-bounce text-black" />
                  </p>
                </div>
                <iframe
                  key={videoId}
                  className="aspect-video w-full max-w-6xl flex-1 rounded-xl"
                  src={`https://youtube.com/embed/${videoId}${
                    autoPlay ? "?autoplay=1" : ""
                  }`}
                ></iframe>
              </div>

              <div className="flex flex-wrap items-stretch gap-10 py-5 lg:flex-col lg:gap-5">
                {recipeVideos.map((video) => (
                  <button
                    key={video.id}
                    onClick={() => {
                      setVideoId(video.id);
                      setAutoPlay(true);
                    }}
                    className="group flex max-w-3xl shrink-0 grow basis-140 items-center gap-2.5 rounded-md border border-solid border-gray-200 bg-gray-100 p-1.5 transition hover:bg-gray-200 lg:max-w-full lg:basis-full"
                  >
                    <PlayIcon className="w-12 shrink-0 text-gray-500 transition group-hover:text-red-500" />
                    <div className="max-w-2xl">
                      <p className="text-xl font-normal">
                        {video.title.replace(" | PG Quick Tips", "")}
                      </p>
                      <p>{video.description}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mb-16 mt-5 w-max rounded-md bg-gray-50 p-2.5">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <QuestionMarkCircleIcon
                      className="w-5 text-gray-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-2 flex-1 md:flex md:justify-between">
                    <p className="text-xl font-normal text-gray-700">
                      Is there anything not yet covered that you would like to
                      see covered?
                    </p>
                    <p className="mt-3 text-xl md:ml-3.5 md:mt-0">
                      <button
                        className="whitespace-nowrap font-medium text-gray-700 hover:text-gray-500"
                        onClick={() => {
                          setSupportOpen(false);

                          (window as any).Intercom(
                            "showNewMessage",
                            "Hi, I would like to see a video covering [insert here]"
                          );
                        }}
                      >
                        Let us know <span aria-hidden="true">&rarr;</span>
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </MajorModal> */}

            <div className="mx-2">
              <Divider className="opacity-50" />
            </div>

            {selectedRecipes.length === sortedRecipes.length ||
            !sortedRecipes.length ? null : (
              <>
                <Tooltip
                  content={
                    <div>
                      <p>Select All Recipes</p>
                      <small>
                        Click to select all recipes in the current view.
                      </small>
                    </div>
                  }
                >
                  <button
                    onClick={() =>
                      setSelectedRecipes(sortedRecipes.map((r) => r.id))
                    }
                    className="group relative"
                  >
                    <Squares2X2Icon className="icon-md icon-gray block group-hover:hidden" />
                    <Squares2X2IconSolid className="icon-md icon-gray hidden group-hover:block" />
                  </button>
                </Tooltip>

                <div className="mx-2">
                  <Divider className="opacity-50" />
                </div>
              </>
            )}

            <Link prefetch={false} href="/archived-recipes">
              <Tooltip content="See Archived Recipes">
                <button>
                  <ArchiveBoxIcon className="icon icon-gray" />
                </button>
              </Tooltip>
            </Link>

            <Link prefetch={false} href="/recipe-history">
              <Tooltip content="See Deleted Recipes">
                <button>
                  <HistoryIcon className="icon icon-gray hover:-rotate-12" />
                </button>
              </Tooltip>
            </Link>

            <div className="mx-2">
              <Divider className="opacity-50" />
            </div>

            <Tooltip content="Create A Recipe Folder">
              <button onClick={openCreateRecipeFolder} className="relative">
                <FolderPlusIcon className="icon-md icon-gray" />
              </button>
            </Tooltip>

            <Link prefetch={false} href="/discover">
              <Tooltip content="Discover Recipes Shared by Other Schools!">
                <button className="btn group flex max-w-max transform-gpu items-center justify-center gap-1.5 rounded-lg bg-gradient-to-tr from-cyan-100 to-green-100 p-1.5 font-semibold text-cyan-600 transition hover:scale-105 hover:text-cyan-700">
                  <ExploreIcon className="w-5 text-cyan-500 group-hover:text-cyan-600" />
                  Discover
                </button>
              </Tooltip>
            </Link>

            <Link prefetch={false} href="/create-recipe">
              <Tooltip content="Create Another Recipe">
                <button className="btn group mx-1.5 flex max-w-max transform-gpu items-center justify-center gap-1.5 rounded-lg bg-gradient-to-tr p-1.5 font-normal transition hover:scale-105">
                  <PlusCircleIcon className="w-5" />
                  Create
                </button>
              </Tooltip>
            </Link>

            <div className="size-8" />

            <Settings
              user={user}
              recipes={recipes}
              sortedFolders={sortedFolders}
              view={view}
              setView={setView}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              selectedRecipes={selectedRecipes}
              openCreateRecipeFolder={openCreateRecipeFolder}
              openEditRecipeFolder={openEditRecipeFolder}
              openDeleteRecipeFolder={openDeleteRecipeFolder}
            />
          </div>
        </TitleBar>

        <CreateRecipeFolderDialog
          user={user}
          sortedFolders={sortedFolders}
          isOpen={createRecipeFolderOpen}
          onClose={closeCreateRecipeFolder}
          currentFolder={folder || undefined}
        />
        <EditRecipeFolderDialog
          user={user}
          folderID={editRecipeFolderOpen}
          sortedFolders={sortedFolders}
          isOpen={!!editRecipeFolderOpen}
          onClose={closeEditRecipeFolder}
        />
        <DeleteRecipeFolderDialog
          user={user}
          folderID={deleteRecipeFolderOpen}
          sortedFolders={sortedFolders}
          isOpen={!!deleteRecipeFolderOpen}
          onClose={closeDeleteRecipeFolder}
        />

        {/* Actions Bar */}
        {/* <div
          className={classNames(
            "overflow-hidden transition-all",
            selectedRecipes.length ? "h-12" : "h-0"
          )}
        >
          <div className="flex h-12 items-center justify-between gap-2.5 px-1.5">
            {selectedRecipes.length ? (
              <div className="flex items-center gap-1.5 justify-self-start">
                <p className="font-normal text-gray-500">
                  {selectedRecipes.length} Recipes Selected
                </p>
                <button
                  className="btn btn-sm btn-subtle"
                  onClick={() => setSelectedRecipes([])}
                >
                  Clear Selection
                </button>
              </div>
            ) : (
              <span />
            )}

          </div>
        </div> */}

        {/* TODO: */}
        {/* Students can only select, if they can edit */}
        {/* Double check selected to make sure actions can be taken */}
        {/* Duplicte/Archive recipes in bulk */}
        {/* Delete recipes in bulk */}
        <div
          className={classNames(
            "fixed right-0 top-14 z-20 w-full max-w-md transform px-2 transition-all",
            selectedRecipes.length ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div
            className={classNames(
              "overflow-auto rounded-2xl border bg-white p-6 text-left align-middle shadow-xl"
            )}
          >
            <div className="mb-5 flex items-center justify-between">
              <h3>{selectedRecipes.length} Recipes Selected</h3>

              <div className="flex flex-col items-end gap-2">
                <button
                  type="button"
                  className="text-sm font-medium text-gray-600 hover:text-gray-900"
                  onClick={() => setSelectedRecipes([])}
                >
                  Clear Selection
                </button>

                {/* If not all recipes are selected, show button to select all recipes */}
                {selectedRecipes.length !== sortedRecipes.length ? (
                  <>
                    <div className="w-8 rounded border border-gray-300" />

                    <button
                      type="button"
                      className="text-sm font-medium text-gray-600 hover:text-gray-900"
                      onClick={() =>
                        setSelectedRecipes(sortedRecipes.map((r) => r.id))
                      }
                    >
                      Select All
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            {/* Lock/Unlock */}
            {isTeacher(user) ? (
              <section className="mb-5">
                {!recipes
                  .filter((r) => selectedRecipes.includes(r.id))
                  .every((r) => r.locked) ? (
                  <LockAllRecipesButton
                    recipeIDs={
                      selectedRecipes.length
                        ? selectedRecipes
                        : recipes.map((r) => r.id)
                    }
                    selected={!!selectedRecipes.length}
                  />
                ) : null}
                {!recipes
                  .filter((r) => selectedRecipes.includes(r.id))
                  .every((r) => !r.locked) ? (
                  <UnlockAllRecipesButton
                    recipeIDs={
                      selectedRecipes.length
                        ? selectedRecipes
                        : recipes.map((r) => r.id)
                    }
                    selected={!!selectedRecipes.length}
                  />
                ) : null}
              </section>
            ) : null}

            <hr className="mb-5" />

            <section className="mb-5">
              <a
                href={`https://recipe.classcart.com/many?ids=${selectedRecipes.join(
                  ","
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                <button className="btn btn-subtle">
                  <PrinterIcon className="mr-0.5 w-5" /> Load in
                  Printer-Friendly Format (One Page)
                  <span className="tag tag-primary">new</span>
                </button>
              </a>
              {/* <button className="btn btn-subtle"
    onClick={() => {
      const newWindow = window.open(`https://recipe.classcart.com/many?ids=${selectedRecipes.join(",")}&printer=1`, '_blank');
      if (newWindow) {
        newWindow.focus();
      }
    }}
  >
    <PrinterIcon className="w-5 mr-0.5" /> Load in Printer-Friendly Format (Many Tabs)
  </button> */}
            </section>

            <hr className="mb-5" />

            {/* Bulk Order */}
            {selectedRecipes.length ? (
              <>
                <BulkOrder
                  user={user}
                  selectedRecipes={
                    selectedRecipes.map((r) =>
                      sortedRecipes.find((sr) => sr.id === r)
                    ) as OrderCarouselQuery["recipes"]
                  }
                  classes={unsortedClasses}
                />
                <hr className="mb-5" />
              </>
            ) : null}

            {/* Move To Folder */}
            {sortedFolders.length ? (
              <section className="mb-5">
                <h4 className="font-normal">Move To Folder</h4>
                <MoveSelectedRecipesToFolder
                  user={user}
                  folder={folder}
                  sortedFolders={sortedFolders}
                  recipeIDs={selectedRecipes}
                  onCompleted={(folderID, recipeIDs) => {
                    setSelectedRecipes([]);
                  }}
                />
              </section>
            ) : null}
          </div>
        </div>

        {recipes.length ? (
          <div
            className="flex h-max items-center justify-between"
            style={{ height: "50px" }}
          >
            <SearchContainer className="top-14 flex items-center">
              <SearchInputIcon />
              <FocusTextInput>
                {(ref, isFocused) => (
                  <input
                    ref={ref}
                    type="text"
                    value={search}
                    placeholder={
                      isFocused
                        ? "Search for Recipes"
                        : 'Search for Recipes (Press "/" to Focus)'
                    }
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 rounded border-none bg-white/50 px-5 py-3 pl-10 shadow focus:bg-white"
                  />
                )}
              </FocusTextInput>
            </SearchContainer>
            <div className="flex items-center justify-end gap-1.5">
              {filteredTags.map((ft) => (
                <span
                  key={ft.value}
                  className="tag group/tag flex items-center justify-center gap-0 transition-all hover:gap-1.5"
                >
                  {ft.label}
                  <button
                    onClick={() => {
                      setFilteredTags(
                        filteredTags.filter((c) => c.value !== ft.value)
                      );
                    }}
                  >
                    <XMarkIcon className="icon m-0 w-0 cursor-pointer transition-all group-hover/tag:w-5" />
                  </button>
                </span>
              ))}
              {filteredClasses.map((fc) => (
                <span
                  key={fc.value}
                  className="tag tag-cyan group/classtag flex items-center justify-center gap-0 transition-all hover:gap-1.5"
                >
                  {fc.obj.name}
                  <button
                    onClick={() => {
                      setFilteredClasses(
                        filteredClasses.filter((c) => c.value !== fc.value)
                      );
                    }}
                  >
                    <XMarkIcon className="icon m-0 w-0 cursor-pointer transition-all group-hover/classtag:w-5" />
                  </button>
                </span>
              ))}
              <AsyncSelect
                className="radio-select w-64"
                classNamePrefix="react-select"
                isSearchable={true}
                value={currentClass}
                defaultOptions
                loadOptions={(inputValue, callback) => {
                  callback([
                    ...searchClasses(
                      inputValue,
                      unsortedClasses.map(shapeClass)
                    ),
                    ...searchTags(inputValue, tags),
                  ]);
                }}
                noOptionsMessage={() => "No Options"}
                maxMenuHeight={200}
                placeholder="Filter by Class or Tag..."
                onChange={(data) => {
                  if (data?.value) {
                    if (data.type === "shaped-class") {
                      setFilteredClasses([...filteredClasses, data]);
                    } else {
                      setFilteredTags([...filteredTags, data]);
                    }
                  }
                }}
                styles={{
                  container: (provided) => ({
                    ...provided,
                    position: "sticky",
                    top: "0px",
                    marginTop: "0",
                  }),
                  control: (styles) => ({
                    ...styles,
                    backgroundColor: "white",
                  }),
                  option: (styles, { data }) =>
                    data.type === "shaped-class"
                      ? {
                          ...styles,
                          ...dot(...(data?.colors || [])),
                        }
                      : { ...styles },
                  input: (styles) => ({
                    ...styles,
                    height: "35px",
                    ...dot(),
                  }),
                  placeholder: (styles) => ({ ...styles, ...dot() }),
                  singleValue: (provided, { data }) =>
                    data.type === "shaped-class"
                      ? {
                          ...provided,
                          ...dot(...(data?.colors || [])),
                        }
                      : { ...provided },
                }}
              />
              <Tooltip
                content={
                  <div>
                    <div className="mb-6 font-semibold">
                      <p className="text-xl">
                        Filter Mode:{" "}
                        {isFilterExclusive ? "Exclusive" : "Inclusive"}
                      </p>
                      <small>Click to toggle the filter mode.</small>
                    </div>
                    <div className="space-y-1.5 font-normal">
                      <p>
                        <span className="font-semibold">Inclusive:</span>{" "}
                        Include all recipes that match any of the filter tags or
                        classes.
                      </p>
                      <p>
                        <span className="font-semibold">Exclusive:</span> Only
                        include recipes matching all filter tags and classes.
                      </p>
                    </div>
                  </div>
                }
              >
                <button
                  onClick={() => {
                    setFilterExclusive(!isFilterExclusive);
                  }}
                >
                  <ExcludeIcon
                    className={classNames(
                      "mx-2 w-5 transform-gpu transition hover:scale-110 hover:text-gray-500",
                      isFilterExclusive ? "text-gray-700" : "text-gray-300"
                    )}
                  />
                </button>
              </Tooltip>

              <div className="size-8" />
            </div>
          </div>
        ) : null}
      </div>
      {fetching && !recipes ? (
        <div className="spinner" />
      ) : error ? (
        <ErrorMessage error={error} />
      ) : (
        <>
          {recipes.length ||
          sortedFolders.filter((rc) =>
            folder ? rc.belongsToId === folder.id : !rc.belongsToId
          ).length ? (
            view === "list" ? (
              <RecipesList
                user={user}
                fragmentID={fragmentID || undefined}
                recipes={recipes}
                sortedRecipes={sortedRecipes}
                selectedRecipes={selectedRecipes}
                setSelectedRecipes={setSelectedRecipes}
                limit={limit}
                setLimit={setLimit}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                folder={folder}
                sortedFolders={sortedFolders}
                openEditRecipeFolder={openEditRecipeFolder}
                openDeleteRecipeFolder={openDeleteRecipeFolder}
                search={search}
              />
            ) : (
              <RecipesGrid
                user={user}
                fragmentID={fragmentID || undefined}
                recipes={recipes}
                sortedRecipes={sortedRecipes}
                selectedRecipes={selectedRecipes}
                setSelectedRecipes={setSelectedRecipes}
                limit={limit}
                setLimit={setLimit}
                folder={folder}
                sortedFolders={sortedFolders}
                openCreateRecipeFolder={openCreateRecipeFolder}
                openEditRecipeFolder={openEditRecipeFolder}
                openDeleteRecipeFolder={openDeleteRecipeFolder}
                search={search}
              />
            )
          ) : null}
          {recipes.length ? null : (
            <div className="flex flex-col items-center justify-center py-18">
              <UndrawChef className="w-56" />
              <div className="mt-20 text-center">
                <h1 className="mb-2">No Recipes Created... Yet</h1>
                {/* <p className="mb-8">Create recipes and begin managing....</p> */}
                <div className="mt-8 flex items-center justify-center gap-4">
                  <Link
                    prefetch={false}
                    href="/create-recipe"
                    className="btn btn-primary font-normal"
                  >
                    <DishIcon className="w-6 text-green-800" /> Create Your
                    First Recipe
                  </Link>

                  <div
                    className="btn font-normal"
                    onClick={openCreateRecipeFolder}
                  >
                    Create A Recipe Folder
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface CreateRecipeFolderDialogProps {
  user: CurrentUser;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  currentFolder?: NonNullable<CurrentUser["school"]>["recipeCollections"][0];
  isOpen: boolean;
  onClose: () => void;
}

const CreateRecipeFolderDialog: React.FC<CreateRecipeFolderDialogProps> = ({
  user,
  sortedFolders,
  currentFolder,
  isOpen,
  onClose,
}) => {
  return (
    <DialogComponent isOpen={isOpen} onClose={onClose}>
      <header className="sticky top-0 flex w-full items-center justify-between py-2">
        <DialogTitle className="font-normal">Create Recipe Folder</DialogTitle>
        <button
          type="button"
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
          onClick={onClose}
        >
          Close
        </button>
      </header>

      <Description className="max-w-2xl pb-5 font-normal text-gray-500">
        <span className="mb-1">
          Recipe folders are a great way to organize your recipes. You can
          create a folder for a specific class, a type of recipe, or any other
          way you want to organize your recipes.
        </span>
        <span>
          You can also move recipes between folders, and even create
          sub-folders.
        </span>
      </Description>

      <div className="p-4">
        <CreateRecipeFolder
          user={user}
          sortedFolders={sortedFolders}
          currentFolder={currentFolder}
          onClose={onClose}
        />
      </div>
    </DialogComponent>
  );
};

interface CreateRecipeFolderProps {
  user: CurrentUser;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  currentFolder?:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null;
  onClose: () => void;
  onComplete?: (folderID: string) => void;
}

const CreateRecipeFolder: React.FC<CreateRecipeFolderProps> = ({
  user,
  sortedFolders,
  onClose,
  onComplete,
  currentFolder,
}) => {
  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  const [folderID, setFolderID] = useState<string>(cuid());

  return (
    <form
      className="max-w-sm space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        const folderName = e.target["new-folder-name"].value as string | null;
        const parentFolder = e.target["nested-folder"]?.value as string | null;

        if (folderName) {
          updateSchool({
            where: { id: user.school?.id },
            data: {
              recipeCollections: {
                create: [
                  {
                    id: folderID,
                    name: folderName,
                    belongsTo: parentFolder
                      ? { connect: { id: parentFolder } }
                      : undefined,
                  },
                ],
              },
            },
          }).then(({ error }) => {
            if (!error) {
              onClose();
              if (onComplete) {
                onComplete(folderID);
              }

              toast.success("Folder created successfully!");
            } else {
              toast.error("Error creating folder");
            }
          });
        }
      }}
    >
      <label htmlFor="new-folder-name" className="block">
        <p className="font-normal text-gray-600">Folder Name</p>

        <input
          type="text"
          id="new-folder-name"
          name="new-folder-name"
          required
          autoFocus
          minLength={3}
          // cant have "/" or "\" or ">" or "<" in the name
          pattern="^[^\/\\\>\<]{3,}$"
          placeholder="Easter"
          className="input w-full rounded border-gray-200"
        />
      </label>

      {sortedFolders.length ? (
        <label htmlFor="nested-folder" className="block">
          <p className="font-normal text-gray-600">
            Where would you like to create this folder?
          </p>

          <select
            id="nested-folder"
            name="nested-folder"
            className="select input w-full rounded border-gray-200"
            defaultValue={currentFolder?.id || ""}
          >
            <option value="">/</option>
            {sortedFolders.map((rc) => (
              <option key={rc.id} value={rc.id}>
                {getPathToFolder(rc, sortedFolders)
                  .map(
                    (id) =>
                      sortedFolders.find((c) => c.id === id)?.name ||
                      "unknown folder"
                  )
                  .join(" / ")}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn btn-subtle" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={fetching}>
          Create Folder
        </button>
      </div>

      {error ? <ErrorMessage error={error} /> : null}
    </form>
  );
};

interface EditRecipeFolderDialogProps {
  user: CurrentUser;
  folderID: string | null;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  isOpen: boolean;
  onClose: () => void;
}

const EditRecipeFolderDialog: React.FC<EditRecipeFolderDialogProps> = ({
  user,
  folderID,
  sortedFolders,
  isOpen,
  onClose,
}) => {
  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  const folder = sortedFolders.find((rc) => rc.id === folderID);

  return (
    <DialogComponent isOpen={isOpen} onClose={onClose}>
      <header className="sticky top-0 flex w-full items-center justify-between py-2">
        <DialogTitle className="font-normal">
          Edit &quot;{folder?.name || "unknonw"}&quot; Recipe Folder
        </DialogTitle>
        <button
          type="button"
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
          onClick={onClose}
        >
          Close
        </button>
      </header>

      <Description className="max-w-2xl pb-5 font-normal text-gray-500">
        Edit the name of the folder, or move it to a different folder.
      </Description>

      {!folder ? null : (
        <form
          className="max-w-sm space-y-5 p-4"
          onSubmit={(e) => {
            e.preventDefault();

            const folderName = e.target["new-folder-name"].value as
              | string
              | null;
            const parentFolder = e.target["nested-folder"]?.value as
              | string
              | null;

            if (
              folderName === folder.name &&
              ((!parentFolder && !folder.belongsToId) ||
                parentFolder === folder.belongsToId)
            ) {
              onClose();
              return;
            }

            if (folderName) {
              updateSchool({
                where: { id: user.school?.id },
                data: {
                  recipeCollections: {
                    update: [
                      {
                        where: { id: folder.id },
                        data: {
                          name: { set: folderName },
                          belongsTo: parentFolder
                            ? { connect: { id: parentFolder } }
                            : folder.belongsToId
                            ? {
                                disconnect: {
                                  id: { equals: folder.belongsToId },
                                },
                              }
                            : undefined,
                        },
                      },
                    ],
                  },
                },
              }).then(({ error }) => {
                if (!error) {
                  onClose();
                  toast.success("Folder edited successfully!");
                } else {
                  toast.error("Error editing folder");
                }
              });
            }
          }}
        >
          <label htmlFor="new-folder-name" className="block">
            <p className="font-normal text-gray-600">Folder Name</p>

            <input
              type="text"
              required
              id="new-folder-name"
              name="new-folder-name"
              minLength={3}
              // cant have "/" or "\" or ">" or "<" in the name
              pattern="^[^\/\\\>\<]{3,}$"
              defaultValue={folder.name}
              className="input w-full rounded border-gray-200"
            />
          </label>

          {sortedFolders.filter((f) => f.id !== folder.id).length ? (
            <label htmlFor="nested-folder" className="block">
              <p className="font-normal text-gray-600">
                Where should this folder be located?
              </p>

              <select
                id="nested-folder"
                name="nested-folder"
                className="select input w-full rounded border-gray-200"
                defaultValue={folder.belongsToId || ""}
              >
                <option value="">/</option>
                {sortedFolders
                  .filter((f) => f.id !== folder.id)
                  .map((rc) => (
                    <option key={rc.id} value={rc.id}>
                      {getPathToFolder(rc, sortedFolders)
                        .map(
                          (id) =>
                            sortedFolders.find((c) => c.id === id)?.name ||
                            "unknown folder"
                        )
                        .join(" / ")}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn btn-subtle" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={fetching}
            >
              Confirm Changes
            </button>
          </div>

          {error ? <ErrorMessage error={error} /> : null}
        </form>
      )}
    </DialogComponent>
  );
};

interface DeleteRecipeFolderDialogProps {
  user: CurrentUser;
  folderID: string | null;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  isOpen: boolean;
  onClose: () => void;
}

const DeleteRecipeFolderDialog: React.FC<DeleteRecipeFolderDialogProps> = ({
  user,
  folderID,
  sortedFolders,
  isOpen,
  onClose,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  const folder = sortedFolders.find((rc) => rc.id === folderID);

  return (
    <DialogComponent isOpen={isOpen} onClose={onClose}>
      <header className="sticky top-0 flex w-full items-center justify-between py-2">
        <DialogTitle className="font-normal">
          Delete &quot;{folder?.name || "unknonw"}&quot; Recipe Folder
        </DialogTitle>
        <button
          type="button"
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
          onClick={onClose}
        >
          Close
        </button>
      </header>

      <Description className="max-w-2xl pb-5 font-normal text-gray-500">
        <span className="mb-1 block">
          Are you sure you want to delete this folder? This action cannot be
          undone.
        </span>
        <span className="block">
          All contents of this folder will be moved to the root folder.
        </span>
      </Description>

      {!folder ? null : (
        <form
          className="max-w-sm space-y-5 p-4"
          onSubmit={(e) => {
            e.preventDefault();

            // first disconnect the subfolders from the parent folder
            updateSchool({
              where: { id: user.school?.id },
              data: {
                recipeCollections: {
                  update: sortedFolders.map((rc) => ({
                    where: { id: rc.id },
                    data: {
                      belongsTo: { disconnect: { id: { equals: folder.id } } },
                    },
                  })),
                },
              },
            }).then(({ error }) => {
              if (!error) {
                // then delete the parent folder
                updateSchool({
                  where: { id: user.school?.id },
                  data: {
                    recipeCollections: {
                      delete: [
                        {
                          id: folder.id,
                        },
                      ],
                    },
                  },
                }).then(({ error }) => {
                  if (!error) {
                    onClose();
                    toast.success("Folder deleted successfully!");

                    if (searchParams?.get("folder") === folderID) {
                      router.push("/recipes");
                    }
                  } else {
                    toast.error("Error deleting folder");
                  }
                });
              } else {
                toast.error("Error deleting folder");
              }
            });
          }}
        >
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn btn-subtle" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={fetching}
            >
              Yes, Delete Folder
            </button>
          </div>

          {error ? <ErrorMessage error={error} /> : null}
        </form>
      )}
    </DialogComponent>
  );
};

interface MoveSelectedRecipesToFolderProps {
  user: CurrentUser;
  folder?: NonNullable<CurrentUser["school"]>["recipeCollections"][0] | null;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  recipeIDs: string[];
  onCompleted?: (folderID: string | null, recipeIDs: string[]) => void;
}

const MoveSelectedRecipesToFolder: React.FC<
  MoveSelectedRecipesToFolderProps
> = ({ user, folder, sortedFolders, recipeIDs, onCompleted }) => {
  const [creating, setCreating] = useState(false);

  const [{ fetching }, moveManyRecipesToCollection] =
    useMoveManyRecipesToCollectionMutation();

  return creating ? (
    <>
      <CreateRecipeFolder
        user={user}
        sortedFolders={sortedFolders}
        currentFolder={folder}
        onClose={() => null}
        onComplete={async (folderID) => {
          await moveManyRecipesToCollection({
            recipeIDs,
            collectionID: folderID,
          }).then(({ error }) => {
            if (!error) {
              toast.success("Recipes moved successfully!");
              onCompleted?.(folderID, recipeIDs);
            } else {
              toast.error("Error moving recipes");
            }
          });
        }}
      />
      <button
        type="button"
        className="text-sm font-normal text-gray-400 hover:text-gray-800"
        onClick={() => setCreating(false)}
      >
        Move to existing folder?
      </button>
    </>
  ) : (
    <form
      className=""
      onSubmit={async (e) => {
        e.preventDefault();
        const folderID = e.target["move-to-folder"].value as string | null;

        console.log("folderID", folderID);

        await moveManyRecipesToCollection({
          recipeIDs,
          collectionID: folderID,
        }).then(({ error }) => {
          if (!error) {
            toast.success("Recipes moved successfully!");
            onCompleted?.(folderID, recipeIDs);
          } else {
            toast.error("Error moving recipes");
          }
        });
      }}
    >
      <label htmlFor="move-to-folder" className="block">
        <p className="font-normal text-gray-500">
          Select a folder to move the{" "}
          {recipeIDs.length > 1 ? "selected recipes" : "recipe"} to.
        </p>
      </label>

      <div className="flex w-full max-w-xs gap-2.5">
        <FolderSelectInput
          folder={folder}
          sortedFolders={sortedFolders}
          //
          id="move-to-folder"
          name="move-to-folder"
          className="min-w-34 max-w-68 border-gray-200"
          disabled={fetching}
        />

        <button
          type="submit"
          className="btn btn-primary shrink-0"
          disabled={fetching}
        >
          {fetching ? <div className="spinner mr-2" /> : null}
          Move Recipe{recipeIDs.length > 1 ? "s" : ""}
        </button>
      </div>

      <button
        type="button"
        className="mt-2 text-sm font-normal text-gray-400 hover:text-gray-800"
        onClick={() => setCreating(true)}
      >
        Move to new folder?
      </button>
    </form>
  );
};

interface MoveRecipesToFolderDialogProps {
  user: CurrentUser;
  folder?: NonNullable<CurrentUser["school"]>["recipeCollections"][0] | null;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  isOpen: boolean;
  onClose: () => void;
  recipes: OrderCarouselQuery["recipes"];
}

export const MoveRecipesToFolderDialog: React.FC<
  MoveRecipesToFolderDialogProps
> = ({ user, folder, sortedFolders, isOpen, onClose, recipes }) => {
  return !recipes.length ? null : (
    <DialogComponent isOpen={isOpen} onClose={onClose}>
      <header className="sticky top-0 flex w-full items-center justify-between py-2">
        <DialogTitle className="font-normal">
          Move{" "}
          {recipes.length > 1
            ? "The Selected Recipes"
            : `"${recipes[0].name || "unknown"}"`}{" "}
        </DialogTitle>
        <button
          type="button"
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
          onClick={onClose}
        >
          Close
        </button>
      </header>

      {/* <Description className="max-w-2xl pb-5 font-normal text-gray-500">
        <span className="mb-1 block">
          Are you sure you want to delete this folder? This action cannot be
          undone.
        </span>
        <span className="block">
          All contents of this folder will be moved to the root folder.
        </span>
      </Description> */}

      <MoveSelectedRecipesToFolder
        user={user}
        folder={folder}
        sortedFolders={sortedFolders}
        recipeIDs={recipes.map((r) => r.id)}
      />
    </DialogComponent>
  );
};

export default RecipesPage;
